import { StrictMode, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Link, Outlet, RouterProvider, useLocation } from "react-router";
import { RouteError, reloadForNewBuild } from "./components/RouteError";
import { Shell } from "./components/Shell";
import { Empty, ErrorBox, Loading } from "./components/ui";
import { ApiError, api, setCsrf } from "./lib/api";
import { type Session, SessionProvider } from "./lib/session";
import { MODULES } from "./nav";
import { screenRoutes } from "./routes";
import { Login } from "./routes/Login";
import "./styles/prototype.css";

/** Loads /me; unauthenticated users go to /login. */
function AuthGate() {
  const [me, setMe] = useState<Session | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const load = useCallback(() => {
    api
      .get<Session>("/me")
      .then((m) => {
        setCsrf(m.csrf);
        setMe(m);
      })
      .catch((e: Error) => {
        if (e instanceof ApiError && e.status === 401) return; // api.ts redirects to /login
        setError(e);
      });
  }, []);
  useEffect(load, [load]);
  if (error)
    return (
      <div className="page">
        <ErrorBox error={error} retry={load} />
      </div>
    );
  if (!me)
    return (
      <div className="page">
        <Loading rows={2} />
      </div>
    );
  return (
    <SessionProvider me={me} refresh={load}>
      <Outlet />
    </SessionProvider>
  );
}

/** Unknown paths. Sidebar screens that have no route yet say so instead of "doesn't exist". */
function NotFound() {
  const { pathname } = useLocation();
  const planned = MODULES.flatMap((m) => m.groups.flatMap((g) => g.items)).find(
    (i) => i.to !== "/" && (pathname === i.to || pathname.startsWith(`${i.to}/`)),
  );
  return (
    <div className="page">
      <Empty
        action={
          <Link className="btn sm" to="/">
            Go to Home
          </Link>
        }
      >
        {planned
          ? `${planned.label} is still being built. Its data is already in the database; the screen will appear here as soon as it's ready.`
          : "This screen doesn't exist."}
      </Empty>
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    path: "/invite/:token",
    lazy: () => import("./routes/platform/AcceptInvite").then((m) => ({ Component: m.AcceptInvite })),
  },
  {
    path: "/forgot-password",
    lazy: () => import("./routes/platform/ResetPassword").then((m) => ({ Component: m.ForgotPassword })),
  },
  {
    path: "/reset-password/:token",
    lazy: () => import("./routes/platform/ResetPassword").then((m) => ({ Component: m.ResetPassword })),
  },
  {
    element: <AuthGate />,
    errorElement: <RouteError />,
    children: [
      {
        element: <Shell />,
        // a screen that fails shows its error inside the shell, the sidebar still there
        children: [
          { errorElement: <RouteError />, children: [...screenRoutes, { path: "*", element: <NotFound /> }] },
        ],
      },
    ],
  },
]);

// a page file of an older build could not be preloaded (a deploy happened): reload to get the new one
window.addEventListener("vite:preloadError", (e) => {
  if (reloadForNewBuild()) e.preventDefault();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
